import { useEffect } from 'react';
import { getDb } from '@data/db/database';
import { getSyncEngine, getSyncProvider } from '@data/sync/syncConfig';
import { useActiveBusiness } from '@features/documents/useDocuments';

/** Opportunistic sync on launch, network recovery, focus, and a modest poll. */
export function SyncCoordinator() {
  const { data: businessId } = useActiveBusiness();

  useEffect(() => {
    if (!businessId) return;
    let running = false;
    let active = true;

    const run = async () => {
      if (!active || running || !navigator.onLine) return;
      running = true;
      try {
        const db = getDb();
        // No provider, no session, or this business never claimed by the
        // signed-in account: nothing to do. Running the engine here would
        // just record a FORBIDDEN error on every poll.
        const provider = await getSyncProvider(db);
        if (provider) {
          const status = await provider.getStatus();
          if (!status.authorisedBusinessIds.includes(businessId)) return;
        }
        const engine = await getSyncEngine(db);
        if (active && engine) await engine.run(businessId, 'automatic');
      } catch (error) {
        console.error('Automatic sync could not start.', error);
      } finally {
        running = false;
      }
    };

    const timer = window.setInterval(() => void run(), 30_000);
    window.addEventListener('online', run);
    window.addEventListener('focus', run);
    void run();

    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('online', run);
      window.removeEventListener('focus', run);
    };
  }, [businessId]);

  return null;
}
